import { z } from 'zod';

import type { ParcelState } from '@nucleus-commerce/api-client/admin';

const delayReason = (required: boolean) =>
  required
    ? z.string().trim().min(1, 'production:validation.delayReason').max(1000)
    : z.string().trim().max(1000);

export function completeStageSchema(requireDelay: boolean) {
  return z.object({
    outcome: z.string(),
    delayReason: delayReason(requireDelay),
    note: z.string().trim().max(2000),
  });
}
export type CompleteStageForm = z.infer<ReturnType<typeof completeStageSchema>>;

export const skipStageSchema = z.object({
  reason: z.string().trim().min(1, 'production:validation.reason').max(500),
});
export type SkipStageForm = z.infer<typeof skipStageSchema>;

/** A note, files for the stage, or both; the backend refuses an empty body. */
export const stageNoteSchema = z
  .object({
    note: z.string().trim().max(2000),
    fileIds: z.array(z.string()).max(20),
  })
  .refine((values) => values.note !== '' || values.fileIds.length > 0, {
    path: ['note'],
    message: 'production:validation.noteOrFiles',
  });
export type StageNoteForm = z.infer<typeof stageNoteSchema>;

export const dueDateSchema = z.object({
  dueAt: z.string().min(1, 'production:validation.due'),
});
export type DueDateForm = z.infer<typeof dueDateSchema>;

export function sendStageSchema(requireDelay: boolean) {
  return z.object({
    mediaIds: z.array(z.string()).min(1, 'production:validation.files').max(10),
    message: z.string().trim().max(2000),
    certificateNumber: z.string().trim().max(120),
    delayReason: delayReason(requireDelay),
  });
}
export type SendStageForm = z.infer<ReturnType<typeof sendStageSchema>>;

const HTTP_URL = /^https?:\/\/\S+$/i;

export interface ParcelLineRow {
  lineId: string;
  /** Typed as text so a blank box reads as zero. */
  quantity: string;
}

export interface ParcelFormRow {
  key: string;
  carrier: string;
  trackingNumber: string;
  trackingUrl: string;
  /** Items a new parcel that carries items ships; empty for parcels without items. */
  lines: ParcelLineRow[];
}

/** An order line's quantity still open to ship. */
export interface OpenLine {
  id: string;
  title: string;
  open: number;
}

export const isParcelFilled = (parcel: ParcelFormRow) =>
  parcel.carrier !== '' && parcel.trackingNumber !== '';

/** A parcel the backend already holds tracking for. */
export const isRecorded = (parcel: Pick<ParcelState, 'carrier' | 'trackingNumber'>) =>
  parcel.carrier !== null && parcel.trackingNumber !== null;

/**
 * A form row for a parcel. A new parcel that carries items starts with every open quantity when
 * it is the only one still to ship items, and with nothing when several share them, so staff
 * split the items on purpose.
 */
export function parcelRow(
  parcel: ParcelState,
  open: readonly OpenLine[] = [],
  shared = false,
): ParcelFormRow {
  const picking = parcel.carriesItems && !isRecorded(parcel);
  return {
    key: parcel.key,
    carrier: parcel.carrier ?? '',
    trackingNumber: parcel.trackingNumber ?? '',
    trackingUrl: parcel.trackingUrl ?? '',
    lines: picking
      ? open.map((line) => ({ lineId: line.id, quantity: shared ? '0' : String(line.open) }))
      : [],
  };
}

const quantityOf = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value) : NaN);

/** The picked quantities of a row, without the zeros; what the request's `lines` carries. */
export function pickedLines(row: ParcelFormRow): Array<{ lineId: string; quantity: number }> {
  return row.lines
    .map((line) => ({ lineId: line.lineId, quantity: quantityOf(line.quantity) }))
    .filter((line) => Number.isFinite(line.quantity) && line.quantity > 0);
}

const sameTracking = (row: ParcelFormRow, recorded: ParcelState) =>
  row.carrier === (recorded.carrier ?? '') &&
  row.trackingNumber === (recorded.trackingNumber ?? '') &&
  row.trackingUrl === (recorded.trackingUrl ?? '');

export interface ParcelsSchemaOptions {
  /** The stage's parcels as the backend holds them, in form order. */
  recorded: readonly ParcelState[];
  /** What is still open on each order line; picked quantities may not exceed it. */
  open?: readonly OpenLine[];
  /** Recorded parcels staff opened for editing; the others are read-only and never sent. */
  editing: ReadonlySet<string>;
  /** The stage ran late with no reason recorded yet. */
  requireDelay: boolean;
}

/**
 * Which rows a submit sends: filled parcels that are new, or opened for editing and changed.
 * Recorded parcels left alone are not re-sent, so the shopper's timeline gets one "shipped"
 * event per parcel.
 */
export function parcelsToSend(
  rows: readonly ParcelFormRow[],
  { recorded, editing }: Pick<ParcelsSchemaOptions, 'recorded' | 'editing'>,
): ParcelFormRow[] {
  return rows.filter((row) => {
    if (!isParcelFilled(row)) return false;
    const previous = recorded.find((p) => p.key === row.key);
    if (!previous || !isRecorded(previous)) return true;
    return editing.has(row.key) && !sameTracking(row, previous);
  });
}

/** True when this submit leaves every parcel tracked, which completes the stage. */
export function completesStage(
  rows: readonly ParcelFormRow[],
  recorded: readonly ParcelState[],
): boolean {
  return rows.every((row) => {
    const previous = recorded.find((p) => p.key === row.key);
    return isParcelFilled(row) || (previous !== undefined && isRecorded(previous));
  });
}

export function parcelsSchema({
  recorded,
  editing,
  requireDelay,
  open = [],
}: ParcelsSchemaOptions) {
  const editable = (key: string) => {
    const previous = recorded.find((p) => p.key === key);
    return !previous || !isRecorded(previous) || editing.has(key);
  };
  return z
    .object({
      parcels: z.array(
        z
          .object({
            key: z.string(),
            carrier: z.string().trim().max(80),
            trackingNumber: z.string().trim().max(120),
            trackingUrl: z
              .string()
              .trim()
              .refine((value) => value === '' || HTTP_URL.test(value), 'production:parcels.url'),
            lines: z.array(
              z.object({
                lineId: z.string(),
                quantity: z
                  .string()
                  .refine(
                    (v) => v.trim() === '' || /^\d+$/.test(v.trim()),
                    'production:parcels.wholeNumber',
                  ),
              }),
            ),
          })
          .superRefine((parcel, ctx) => {
            if (!editable(parcel.key)) return;
            const started =
              parcel.carrier !== '' || parcel.trackingNumber !== '' || parcel.trackingUrl !== '';
            if (!started) return;
            if (parcel.carrier === '') {
              ctx.addIssue({
                code: 'custom',
                path: ['carrier'],
                message: 'production:parcels.both',
              });
            }
            if (parcel.trackingNumber === '') {
              ctx.addIssue({
                code: 'custom',
                path: ['trackingNumber'],
                message: 'production:parcels.both',
              });
            }
          }),
      ),
      delayReason: z.string().trim().max(1000),
    })
    .superRefine((values, ctx) => {
      const sending = parcelsToSend(values.parcels, { recorded, editing });
      const used = new Map<string, number>();
      values.parcels.forEach((row, index) => {
        if (row.lines.length === 0 || !sending.includes(row)) return;
        const picked = pickedLines(row);
        if (picked.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['parcels', index, 'lines'],
            message: 'production:parcels.noItems',
          });
        }
        for (const line of picked)
          used.set(line.lineId, (used.get(line.lineId) ?? 0) + line.quantity);
      });
      values.parcels.forEach((row, index) => {
        row.lines.forEach((line, lineIndex) => {
          const max = open.find((o) => o.id === line.lineId)?.open ?? 0;
          if ((used.get(line.lineId) ?? 0) > max && quantityOf(line.quantity) > 0) {
            ctx.addIssue({
              code: 'custom',
              path: ['parcels', index, 'lines', lineIndex, 'quantity'],
              message: 'production:parcels.overOpen',
            });
          }
        });
      });
      if (sending.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['parcels'],
          message: 'production:parcels.atLeastOne',
        });
      }
      // The backend asks for the reason only when the submit completes the stage.
      if (requireDelay && values.delayReason === '' && completesStage(values.parcels, recorded)) {
        ctx.addIssue({
          code: 'custom',
          path: ['delayReason'],
          message: 'production:validation.delayReason',
        });
      }
    });
}
export type ParcelsForm = z.input<ReturnType<typeof parcelsSchema>>;

export const changeRequestSchema = z.object({
  message: z.string().trim().min(1, 'production:validation.message').max(2000),
  extraCost: z.object({ amount: z.number().int().min(0), currency: z.string() }).nullable(),
  stageKey: z.string(),
});
export type ChangeRequestForm = z.infer<typeof changeRequestSchema>;

export const resolveChangeSchema = z.object({
  note: z.string().trim().min(1, 'production:validation.resolution').max(2000),
  extraCost: z.object({ amount: z.number().int().min(0), currency: z.string() }).nullable(),
});
export type ResolveChangeForm = z.infer<typeof resolveChangeSchema>;
